import React, {
  useRef,
  useMemo,
  useEffect,
  useCallback,
  FC,
}                             from 'react';
import styled                 from 'styled-components';
import {
  withRouter,
  RouteComponentProps,
}                             from 'react-router-dom';
import { useSelector }        from 'react-redux';
import { FormikContextType }  from 'formik';
import { EAppPage }           from '@enums/common';
import { FormikRefContext }   from '@utils/FormikRefContext';
import { getCurrentCUID }     from '@modules/accountManagement/accountManagementReducer';
import { getIsAuthorized }    from '@modules/auth/authReducer';
import { useLogout }          from '@modules/auth/queries/authQueries';
import { navItems, INavItem } from '@utils/navigation';
import { useToggle }          from '@utils/useToggle';
import { Header }             from '../Header/Header';
import { NavBar }             from '../NavBar/NavBar';
import { LogoutWarningModal } from './LogoutWarningModal';

const Content = styled.div`
  flex             : 1 0 auto;
  height           : 100%;
  padding-top      : 110px;
  background-color : ${({ theme: { backgroundColor } }): string => backgroundColor.lightGray};

  @media (min-width: 960px) {
    padding-top: 145px;
  }
`;

interface INavigation {
  breadcrumbs : INavItem[];
  navigation  : INavItem[];
}

const findCrumb = (items: INavItem[], path: string): any =>
  items.find((x: INavItem): boolean => x.crumb === path);

const getNavigation = (pathname: string): INavigation => {
  const pathArray = pathname.match(/[^/?]*[^/?]/g) || [];
  let searchArr: INavItem[] = navItems;

  return pathArray.reduce((acc: INavigation, path: string): INavigation => {
    const navItem = findCrumb(searchArr, path);

    if (navItem) {
      const { children, menu, ...crumb } = navItem;
      acc.breadcrumbs.push(crumb);

      if (children) {
        searchArr = children;
      }

      if (menu) {
        acc.navigation = menu;
      }
    }

    return acc;
  }, {
    breadcrumbs : [],
    navigation  : [...navItems],
  });
};

const replaceId = (x: INavItem, id: string): INavItem => {
  const { path, ...values } = x;
  let newPath;

  if (path) {
    newPath = path.replace('{id}', id);
  }

  return {
    ...values,
    path: newPath,
  };
};

const convertPaths = (
  breadcrumbs : INavItem[],
  navigation  : INavItem[],
  id          : string,
): INavigation => ({
  breadcrumbs : breadcrumbs.map((breadcrumb: INavItem): INavItem => replaceId(breadcrumb, id)),
  navigation  : navigation.map((nav: INavItem): INavItem => replaceId(nav, id)),
});

const AuthWrapperComponent: FC<RouteComponentProps> = ({
  children,
  history,
  location: { pathname },
}) => {
  const isAuthorized = useSelector(getIsAuthorized);
  const currentCUID  = useSelector(getCurrentCUID);

  const formikRef = useRef<FormikContextType<any> | null>(null);

  const [showLogoutWarning, toggleShowLogoutWarning] = useToggle();

  // #region API INTEGRATION
  const { mutateAsync: logout } = useLogout();
  // #endregion

  useEffect(
    (): void => {
      if (!isAuthorized) {
        history.replace({
          pathname : '/',
          state    : {
            from: pathname,
          },
        });
      }
    },
    [history, isAuthorized, pathname],
  );

  const navObject = useMemo(
    (): INavigation => getNavigation(pathname),
    [pathname],
  );

  const { breadcrumbs, navigation } = useMemo(
    (): INavigation => {
      const id = currentCUID || null;

      if (!id) {
        return navObject;
      }

      return convertPaths(navObject.breadcrumbs, navObject.navigation, id);
    },
    [navObject, currentCUID],
  );

  const handleGoHome = useCallback(
    (): void => {
      if (isAuthorized) {
        history.replace(EAppPage.Dashboard);
      }
    },
    [history, isAuthorized],
  );

  const handleLogout = useCallback(
    async (): Promise<void> => {
      if (formikRef.current?.dirty) {
        toggleShowLogoutWarning();
      } else {
        await logout();
      }
    },
    [logout, toggleShowLogoutWarning],
  );

  return (
    <>
      {isAuthorized && (
        <>
          <Header
            history      = {history}
            breadcrumbs  = {breadcrumbs}
            isAuthorized = {isAuthorized}
            handleLogout = {handleLogout}
            handleGoHome = {handleGoHome}
          />

          <NavBar
            history    = {history}
            navigation = {navigation}
          />

          <Content>
            <FormikRefContext.Provider value={formikRef}>
              {children}
            </FormikRefContext.Provider>
          </Content>

          <LogoutWarningModal
            open      = {showLogoutWarning}
            onCancel  = {toggleShowLogoutWarning}
            onConfirm = {async () => logout()}
          />
        </>
      )}
    </>
  );
};

export const AuthWrapper = withRouter(AuthWrapperComponent);
